import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { SONG, SECTION_ORDER } from '@/config/valentineContent';
import HeartParticles from './HeartParticles';
import ValentineHero from './ValentineHero';
import LoveLetter from './LoveLetter';
import ReasonsGrid from './ReasonsGrid';
import LoveContract from './LoveContract';
import VirtualHug from './VirtualHug';
import ScratchCard from './ScratchCard';
import ComplimentGenerator from './ComplimentGenerator';
import PortraitGallery from './PortraitGallery';

import MiniSurprise from './MiniSurprise';
import BirthdayWishes from './BirthdayWishes';
import BigQuestion from './BigQuestion';
import RunawayQuestion from './RunawayQuestion';
import ValentineFooter from './ValentineFooter';
import FadeInSection from './FadeInSection';

export default function ValentineChapter() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const playerRef = useRef(null);

  useEffect(() => {
    if (playerRef.current) return;
    const audio = new Audio(SONG.src);
    audio.loop = true;
    audio.volume = volume / 100;
    audio.addEventListener('play', () => setIsPlaying(true));
    audio.addEventListener('pause', () => setIsPlaying(false));
    playerRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
      playerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p) return;
    if (p.paused) p.play().catch(() => {});
    else p.pause();
  };

  const changeVolume = (v) => {
    setVolume(v);
    if (playerRef.current) playerRef.current.volume = v / 100;
  };

  const savedVolumeRef = useRef(null);
  const fadeRef = useRef(null);

  const fadeOutSong = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    const cur = Math.round(p.volume * 100);
    if (cur <= 0) return;
    savedVolumeRef.current = cur;
    if (fadeRef.current) clearInterval(fadeRef.current);
    let v = cur;
    fadeRef.current = setInterval(() => {
      v = Math.max(0, v - 5);
      p.volume = v / 100;
      if (v <= 0) clearInterval(fadeRef.current);
    }, 30);
  }, []);

  const fadeInSong = useCallback(() => {
    const p = playerRef.current;
    if (!p) return;
    const target = savedVolumeRef.current ?? volume;
    if (fadeRef.current) clearInterval(fadeRef.current);
    let v = Math.round(p.volume * 100);
    fadeRef.current = setInterval(() => {
      v = Math.min(target, v + 5);
      p.volume = v / 100;
      if (v >= target) clearInterval(fadeRef.current);
    }, 30);
  }, [volume]);

  return (
    <div className="valentine-chapter relative min-h-screen overflow-x-hidden main-content-enter" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <HeartParticles />
      <ValentineHero isPlaying={isPlaying} onTogglePlay={togglePlay} volume={volume} onVolumeChange={changeVolume} />
      {SECTION_ORDER.map((id) => {
        switch (id) {
          case 'love-letter':       return <FadeInSection key={id}><LoveLetter /></FadeInSection>;
          case 'reasons':           return <FadeInSection key={id}><ReasonsGrid /></FadeInSection>;
          case 'love-contract':     return <FadeInSection key={id}><LoveContract /></FadeInSection>;
          case 'virtual-hug':       return <FadeInSection key={id}><VirtualHug /></FadeInSection>;
          case 'scratch-card':      return <FadeInSection key={id}><ScratchCard /></FadeInSection>;
          case 'compliments':       return <FadeInSection key={id}><ComplimentGenerator /></FadeInSection>;
          case 'portrait-gallery':  return <FadeInSection key={id}><PortraitGallery /></FadeInSection>;
          case 'mini-surprise':     return <FadeInSection key={id}><MiniSurprise onVideoPlay={fadeOutSong} onVideoStop={fadeInSong} /></FadeInSection>;
          case 'birthday-wishes':   return <FadeInSection key={id}><BirthdayWishes /></FadeInSection>;
          case 'runaway-question':  return <FadeInSection key={id}><RunawayQuestion /></FadeInSection>;
          case 'big-question':      return <FadeInSection key={id}><BigQuestion /></FadeInSection>;
          default: return null;
        }
      })}
      <ValentineFooter />
    </div>
  );
}
